import {Injectable} from '@angular/core';
import {collection, collectionData, doc, docData, Firestore} from '@angular/fire/firestore';
import type {DocumentData, FirestoreDataConverter, QueryDocumentSnapshot} from 'firebase/firestore';
import {combineLatest, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {IDataTimeStamps, IFirebaseDataTimeStamps} from '../models';
import {convertIDataTimeStampsToDates} from '../utils/core.effects.utils';
import {CoreService} from './core.service';

/**
 * Loads per-store data timestamp documents used by the header processing queue.
 */
@Injectable({providedIn: 'root'})
export class StoreDataTimestampsService extends CoreService {
  constructor(protected override firestore: Firestore) {
    super(firestore);
  }

  private static conv<T>(): FirestoreDataConverter<T> {
    return {
      toFirestore: (d: T): DocumentData => d as unknown as DocumentData,
      fromFirestore: (snap: QueryDocumentSnapshot): T => snap.data() as T,
    };
  }

  fetchAllStoreInfo(): Observable<Record<string, IDataTimeStamps>> {
    const storesCol = collection(this.firestore, 'stores')
      .withConverter(StoreDataTimestampsService.conv<{id: string}>());
    const stores$ = collectionData(storesCol, {idField: 'id'});

    return stores$.pipe(
      switchMap((stores) => {
        if (!stores.length) {
          return of([] as {storeId: string; storeInfo?: IFirebaseDataTimeStamps}[]);
        }

        const perStore$: Observable<{storeId: string; storeInfo?: IFirebaseDataTimeStamps}>[] =
          stores.map(({id: storeId}) => {
            const tsRef = doc(
              this.firestore,
              `shared/stores_data/${storeId}/data/singular_documents/data_timestamps`,
            ).withConverter(StoreDataTimestampsService.conv<IFirebaseDataTimeStamps>());

            return docData(tsRef).pipe(map((storeInfo) => ({storeId, storeInfo})));
          });

        return combineLatest(perStore$);
      }),
      map((rows: {storeId: string; storeInfo?: IFirebaseDataTimeStamps}[]) => {
        const out: Record<string, IDataTimeStamps> = {};
        for (const {storeId, storeInfo} of rows) {
          if (storeInfo) {
            out[storeId] = convertIDataTimeStampsToDates(storeInfo);
          }
        }
        return out;
      }),
    );
  }
}
